import { Typography } from '@euler/app/components/typography';
import { ApdmTag } from '@euler/app/flows/apdm/components/ApdmSurface';
import { BottomPanel } from '@euler/components/BottomPanel';
import { kApdmBlockReasonLabels } from '@euler/functions/apdm/campaignLabels';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ApdmPlanAudienceListEntry } from '@euler/model/apdm/build-plan';
import { useServiceFactory } from '@euler/services/factory';
import { ChevronLeft } from 'lucide-react-native';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, View } from 'react-native';
import { aiColors } from '../theme';
import { sampleFacts } from './PlanCampaignCard';

type ListState = {
  customers: ApdmPlanAudienceListEntry[];
  /** Where the next page starts; null once the last page is in. */
  next: string | null;
  loading: boolean;
  failed: boolean;
};

const kFirstPage: ListState = {
  customers: [],
  next: null,
  loading: true,
  failed: false,
};

/** Every customer an audience card counted, read a page at a time. */
export const PlanAudienceListSheet = memo(
  ({
    messageId,
    visible,
    onClose,
  }: {
    messageId: string;
    visible: boolean;
    onClose: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { apdmCampaignService } = useServiceFactory();
    const [state, setState] = useState<ListState>(kFirstPage);
    // One read at a time: the list's end fires again while a page loads.
    const reading = useRef(false);
    // Each opening reads its own list; a page that lands after the sheet
    // closed or reopened belongs to an earlier one and is dropped.
    const generation = useRef(0);
    const read = useCallback(
      async (afterPlate: string | null) => {
        if (reading.current) return;
        const mine = generation.current;
        reading.current = true;
        setState(current => ({ ...current, loading: true, failed: false }));
        try {
          const page = await apdmCampaignService.planAudience({
            messageId,
            afterPlate,
          });
          if (mine !== generation.current) return;
          setState(current => ({
            customers: afterPlate
              ? [...current.customers, ...page.customers]
              : page.customers,
            next: page.nextAfterPlate,
            loading: false,
            failed: false,
          }));
        } catch (error) {
          console.error('APDM plan audience list failed', error);
          if (mine === generation.current)
            setState(current => ({ ...current, loading: false, failed: true }));
        } finally {
          if (mine === generation.current) reading.current = false;
        }
      },
      [apdmCampaignService, messageId],
    );
    useEffect(() => {
      generation.current++;
      reading.current = false;
      if (!visible) return;
      setState(kFirstPage);
      void read(null);
    }, [visible, read]);
    return (
      <BottomPanel visible={visible} onClose={onClose} tall>
        <View style={{ flex: 1, backgroundColor: aiColors.background }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(10),
              paddingHorizontal: rpx(12),
              paddingVertical: rpx(12),
              backgroundColor: aiColors.card,
              borderBottomWidth: 1,
              borderBottomColor: aiColors.border,
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="关闭"
              onPress={onClose}
              hitSlop={8}
            >
              <ChevronLeft size={22} color={aiColors.foreground} />
            </Pressable>
            <Typography.Text
              bold
              size={rpx(15)}
              color={aiColors.foreground}
              style={{ flex: 1 }}
            >
              目标客户
            </Typography.Text>
          </View>
          <FlatList
            // Without its own size the list grows to its rows and the
            // sheet clips it instead of scrolling.
            style={{ flex: 1 }}
            data={state.customers}
            keyExtractor={entry => entry.licensePlateNo}
            renderItem={({ item, index }) => (
              <CustomerRow entry={item} striped={index % 2 === 1} />
            )}
            contentContainerStyle={{
              paddingHorizontal: rpx(16),
              paddingVertical: rpx(8),
            }}
            onEndReachedThreshold={0.5}
            onEndReached={() => {
              if (state.next && !state.loading && !state.failed)
                void read(state.next);
            }}
            ListFooterComponent={
              state.loading ? (
                <ActivityIndicator
                  color={aiColors.primary}
                  style={{ paddingVertical: rpx(20) }}
                />
              ) : state.failed ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => void read(state.next)}
                  style={{ paddingVertical: rpx(20), alignItems: 'center' }}
                >
                  <Typography.Text size={rpx(13)} color={aiColors.primary}>
                    没有读取到，点此重试
                  </Typography.Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      </BottomPanel>
    );
  },
);

const CustomerRow = memo(
  ({
    entry,
    striped,
  }: {
    entry: ApdmPlanAudienceListEntry;
    /** Every other row is shaded, so a long list reads row by row. */
    striped: boolean;
  }) => {
    const { rpx } = useResponsiveContext();
    const excluded = entry.excludedBy.length > 0;
    const facts = sampleFacts(entry.facts);
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: rpx(10),
          paddingVertical: rpx(12),
          paddingHorizontal: rpx(12),
          borderRadius: rpx(8),
          backgroundColor: striped ? aiColors.muted : aiColors.card,
          opacity: excluded ? 0.6 : 1,
        }}
      >
        <View style={{ flex: 1, gap: rpx(3) }}>
          <Typography.Text
            medium
            size={rpx(14)}
            color={aiColors.foreground}
            numberOfLines={1}
          >
            {[entry.licensePlateNo, entry.facts.customerName?.trim()]
              .filter(Boolean)
              .join(' · ')}
          </Typography.Text>
          {excluded || facts ? (
            <Typography.Text
              size={rpx(12)}
              color={aiColors.mutedForeground}
              numberOfLines={1}
            >
              {excluded
                ? entry.excludedBy
                    .map(reason => kApdmBlockReasonLabels[reason])
                    .join('、')
                : facts}
            </Typography.Text>
          ) : null}
        </View>
        {excluded ? (
          <ApdmTag round text="已排除" color={aiColors.mutedForeground} />
        ) : entry.knownReachable ? (
          <ApdmTag round text="已确认" color={aiColors.green} />
        ) : null}
      </View>
    );
  },
);
